import { API, Card, Help, Input, Submit, authenticationService, clearMfaDeviceToken } from '@enkla-gui'
import React, { useCallback, useEffect, useState } from 'react'

import Modal from 'react-modal'
import { icons } from '@config'
import { notify } from '@/_helpers'
import { useForm } from 'react-hook-form'

export default function MfaSettings({ enabled: initialEnabled }) {
	const [enabled, setEnabled] = useState(!!initialEnabled)
	const [devices, setDevices] = useState([])
	const [sending, setSending] = useState(false)
	const [error, setError] = useState(null)
	const [setupModal, setSetupModal] = useState(false)
	const [setupStep, setSetupStep] = useState('password')
	const [setupData, setSetupData] = useState(null)
	const [recoveryCodes, setRecoveryCodes] = useState(null)
	const [disableModal, setDisableModal] = useState(false)
	const [regenModal, setRegenModal] = useState(false)

	const { register: setupRegister, handleSubmit: setupSubmit, reset: resetSetup } = useForm()
	const { register: disableRegister, handleSubmit: disableSubmit, reset: resetDisable } = useForm()
	const { register: regenRegister, handleSubmit: regenSubmit, reset: resetRegen } = useForm()

	const storeRotatedToken = res => {
		authenticationService.loginWithToken({
			access_token: res.access_token,
			token_type: res.token_type,
			expires_in: res.expires_in,
			session_id: res.session_id
		})
	}

	const loadDevices = useCallback(() => {
		if (!enabled) {
			setDevices([])
			return
		}
		API.get('api/auth/mfa/devices')
			.then(setDevices)
			.catch(() => setDevices([]))
	}, [enabled])

	useEffect(() => {
		setEnabled(!!initialEnabled)
	}, [initialEnabled])

	useEffect(() => {
		loadDevices()
	}, [loadDevices])

	const startSetup = data => {
		setSending(true)
		setError(null)
		API.post('api/auth/mfa/setup', data)
			.then(res => {
				setSetupData(res)
				setSetupStep('scan')
				setSending(false)
			})
			.catch(([, res]) => {
				setError({ message: res.message })
				setSending(false)
			})
	}

	const confirmSetup = data => {
		setSending(true)
		setError(null)
		API.post('api/auth/mfa/confirm', { code: data.code })
			.then(res => {
				storeRotatedToken(res)
				setEnabled(true)
				setRecoveryCodes(res.recovery_codes)
				setSetupStep('codes')
				setSending(false)
				notify.toast('Tvåfaktorsautentisering aktiverad')
			})
			.catch(([, res]) => {
				setError({ message: res.message })
				setSending(false)
			})
	}

	const closeSetup = () => {
		const keepCodes = setupStep === 'codes'
		setSetupModal(false)
		setSetupStep('password')
		setSetupData(null)
		if (!keepCodes) setRecoveryCodes(null)
		setError(null)
		resetSetup()
	}

	const disableMfa = data => {
		setSending(true)
		setError(null)
		API.post('api/auth/mfa/disable', data)
			.then(res => {
				storeRotatedToken(res)
				setEnabled(false)
				setDevices([])
				clearMfaDeviceToken()
				setDisableModal(false)
				resetDisable()
				setSending(false)
				notify.toast('Tvåfaktorsautentisering inaktiverad')
			})
			.catch(([, res]) => {
				setError({ message: res.message })
				setSending(false)
			})
	}

	const regenerateCodes = data => {
		setSending(true)
		setError(null)
		API.post('api/auth/mfa/recovery-codes', data)
			.then(res => {
				setRecoveryCodes(res.recovery_codes)
				setRegenModal(false)
				resetRegen()
				setSending(false)
				notify.toast('Nya återställningskoder skapade')
			})
			.catch(([, res]) => {
				setError({ message: res.message })
				setSending(false)
			})
	}

	const revokeDevice = async device => {
		if (!(await notify.confirm('Vill du glömma den här enheten?'))) return
		API.delete(`api/auth/mfa/devices/${device.id}`)
			.then(() => {
				clearMfaDeviceToken()
				notify.toast('Enheten har glömts')
				loadDevices()
			})
			.catch(([, res]) => notify.toast(res.message, 'error'))
	}

	const revokeAllDevices = async () => {
		if (!(await notify.confirm('Vill du glömma alla betrodda enheter?'))) return
		API.delete('api/auth/mfa/devices')
			.then(() => {
				clearMfaDeviceToken()
				notify.toast('Alla enheter har glömts')
				loadDevices()
			})
			.catch(([, res]) => notify.toast(res.message, 'error'))
	}

	const copyCodes = () => {
		if (!recoveryCodes) return
		navigator.clipboard?.writeText(recoveryCodes.join('\n')).then(() => {
			notify.toast('Återställningskoder kopierade')
		})
	}

	const downloadCodes = () => {
		if (!recoveryCodes) return
		const blob = new Blob([recoveryCodes.join('\n')], { type: 'text/plain' })
		const url = URL.createObjectURL(blob)
		const a = document.createElement('a')
		a.href = url
		a.download = 'enkla-fastigheter-aterstallningskoder.txt'
		a.click()
		URL.revokeObjectURL(url)
	}

	return (
		<>
			<Card
				title='Tvåfaktorsautentisering'
				icon='fas fa-shield-alt'
				buttons={{
					footer: enabled
						? [
								{
									callback: () => {
										setError(null)
										setRegenModal(true)
									},
									text: 'Nya återställningskoder',
									bg: 'light'
								},
								{
									callback: () => {
										setError(null)
										setDisableModal(true)
									},
									text: 'Inaktivera',
									bg: 'danger'
								}
						  ]
						: [
								{
									callback: () => {
										setError(null)
										setSetupStep('password')
										setSetupModal(true)
									},
									text: 'Aktivera',
									bg: 'primary'
								}
						  ]
				}}>
				<p className='mb-2'>
					{enabled ? (
						<span className='badge bg-success'>Aktiverad</span>
					) : (
						<span className='badge bg-secondary'>Ej aktiverad</span>
					)}
				</p>
				<p className='text-muted mb-3'>
					Lägg till ett extra skydd vid inloggning med en app som Google Authenticator eller Microsoft Authenticator.
					Efter aktivering behöver du en engångskod från appen varje gång du loggar in (om du inte har valt att komma
					ihåg enheten).
				</p>
				<Help header={false} type='info' icon={icons.warning}>
					<strong>Om du tappar telefonen:</strong>
					<ol className='mb-0 mt-2 ps-3'>
						<li>Använd en återställningskod vid inloggning.</li>
						<li>Be en kontoadministratör återställa MFA under Inställningar → Användare.</li>
						<li>Kontakta vår support på kontakt@enklafastigheter.se om du är ensam administratör.</li>
					</ol>
				</Help>

				{enabled && (
					<div className='mt-4'>
						<div className='d-flex justify-content-between align-items-center mb-2'>
							<h6 className='mb-0'>Betrodda enheter</h6>
							{devices.length > 0 && (
								<button type='button' className='btn btn-sm btn-light' onClick={revokeAllDevices}>
									Glöm alla
								</button>
							)}
						</div>
						{devices.length === 0 ? (
							<p className='text-muted mb-0'>Inga betrodda enheter just nu.</p>
						) : (
							<ul className='list-group'>
								{devices.map(device => (
									<li
										key={device.id}
										className='list-group-item d-flex justify-content-between align-items-center'>
										<div>
											<div className='small'>{device.user_agent || 'Okänd enhet'}</div>
											<div className='text-muted small'>
												{device.ip_address || '—'} · går ut{' '}
												{device.expires_at ? new Date(device.expires_at).toLocaleDateString('sv-SE') : '—'}
											</div>
										</div>
										<button type='button' className='btn btn-sm btn-light' onClick={() => revokeDevice(device)}>
											Glöm
										</button>
									</li>
								))}
							</ul>
						)}
					</div>
				)}
			</Card>

			{recoveryCodes && !setupModal && (
				<Card title='Spara dina återställningskoder' icon='fas fa-key'>
					<Help header={false} type='warning' icon={icons.warning}>
						Spara dessa nu — de visas bara en gång. Varje kod kan användas en gång om du tappar tillgång till din
						autentiseringsapp.
					</Help>
					<pre className='bg-light p-3 rounded'>{recoveryCodes.join('\n')}</pre>
					<div className='d-flex gap-2'>
						<button type='button' className='btn btn-light' onClick={copyCodes}>
							Kopiera
						</button>
						<button type='button' className='btn btn-light' onClick={downloadCodes}>
							Ladda ner
						</button>
						<button type='button' className='btn btn-primary' onClick={() => setRecoveryCodes(null)}>
							Jag har sparat koderna
						</button>
					</div>
				</Card>
			)}

			<Modal isOpen={setupModal} onRequestClose={closeSetup} className='no-padding'>
				<Card
					collapsed={null}
					title='Aktivera tvåfaktorsautentisering'
					icon='fas fa-fw fa-shield-alt'
					buttons={{
						header: [{ callback: closeSetup, icon: 'fas fa-times', text: 'Avbryt', bg: 'light' }],
						footer:
							setupStep === 'password'
								? [<Submit key='mfa-setup-password' onSubmit={setupSubmit(startSetup)} text='Fortsätt' sending={sending} />]
								: setupStep === 'scan'
								? [<Submit key='mfa-setup-confirm' onSubmit={setupSubmit(confirmSetup)} text='Aktivera' sending={sending} />]
								: [{ callback: closeSetup, text: 'Klar', bg: 'primary' }]
					}}>
					{error && (
						<Help header={false} type='warning' icon={icons.warning}>
							{error.message}
						</Help>
					)}
					{setupStep === 'password' && (
						<>
							<p>Bekräfta ditt lösenord för att börja.</p>
							<div className='row mb-3'>
								<Input
									controls={{ register: setupRegister }}
									name='password'
									options={{ required: true }}
									label='Lösenord'
									type='password'
									error={error}
								/>
							</div>
						</>
					)}
					{setupStep === 'scan' && setupData && (
						<>
							<p>Skanna QR-koden med t.ex. Google Authenticator, eller ange nyckeln manuellt.</p>
							<div
								className='text-center mb-3'
								dangerouslySetInnerHTML={{ __html: setupData.qr_svg }}
							/>
							<p className='text-center'>
								<small className='text-muted'>Manuell nyckel</small>
								<br />
								<code>{setupData.secret}</code>
							</p>
							<div className='row mb-3'>
								<Input
									controls={{ register: setupRegister }}
									name='code'
									options={{ required: true }}
									label='Verifieringskod från appen'
									type='text'
									placeholder='000000'
									error={error}
								/>
							</div>
						</>
					)}
					{setupStep === 'codes' && recoveryCodes && (
						<>
							<Help header={false} type='warning' icon={icons.warning}>
								Spara dessa återställningskoder nu — de visas bara en gång.
							</Help>
							<pre className='bg-light p-3 rounded'>{recoveryCodes.join('\n')}</pre>
							<div className='d-flex gap-2 mb-2'>
								<button type='button' className='btn btn-light' onClick={copyCodes}>
									Kopiera
								</button>
								<button type='button' className='btn btn-light' onClick={downloadCodes}>
									Ladda ner
								</button>
							</div>
						</>
					)}
				</Card>
			</Modal>

			<Modal isOpen={disableModal} onRequestClose={() => setDisableModal(false)} className='no-padding'>
				<Card
					collapsed={null}
					title='Inaktivera tvåfaktorsautentisering'
					icon='fas fa-fw fa-shield-alt'
					buttons={{
						header: [{ callback: () => setDisableModal(false), icon: 'fas fa-times', text: 'Avbryt', bg: 'light' }],
						footer: [
							<Submit
								key='mfa-disable'
								onSubmit={disableSubmit(disableMfa)}
								text='Inaktivera'
								sending={sending}
							/>
						]
					}}>
					{error && (
						<Help header={false} type='warning' icon={icons.warning}>
							{error.message}
						</Help>
					)}
					<div className='row mb-3'>
						<Input
							controls={{ register: disableRegister }}
							name='password'
							options={{ required: true }}
							label='Lösenord'
							type='password'
						/>
					</div>
					<div className='row mb-3'>
						<Input
							controls={{ register: disableRegister }}
							name='code'
							options={{ required: true }}
							label='Verifieringskod eller återställningskod'
							type='text'
						/>
					</div>
				</Card>
			</Modal>

			<Modal isOpen={regenModal} onRequestClose={() => setRegenModal(false)} className='no-padding'>
				<Card
					collapsed={null}
					title='Nya återställningskoder'
					icon='fas fa-fw fa-key'
					buttons={{
						header: [{ callback: () => setRegenModal(false), icon: 'fas fa-times', text: 'Avbryt', bg: 'light' }],
						footer: [
							<Submit
								key='mfa-regenerate-codes'
								onSubmit={regenSubmit(regenerateCodes)}
								text='Skapa nya koder'
								sending={sending}
							/>
						]
					}}>
					<p>Dina gamla återställningskoder slutar gälla när du skapar nya.</p>
					{error && (
						<Help header={false} type='warning' icon={icons.warning}>
							{error.message}
						</Help>
					)}
					<div className='row mb-3'>
						<Input
							controls={{ register: regenRegister }}
							name='password'
							options={{ required: true }}
							label='Lösenord'
							type='password'
						/>
					</div>
					<div className='row mb-3'>
						<Input
							controls={{ register: regenRegister }}
							name='code'
							options={{ required: true }}
							label='Verifieringskod'
							type='text'
						/>
					</div>
				</Card>
			</Modal>
		</>
	)
}
